import type { ThemeConfig } from "@/types/theme";

export const tailwindViolet: ThemeConfig = {
  classes: {
    // Color classes
    primary: "text-violet-700 dark:text-violet-400",
    secondary: "text-violet-900 dark:text-violet-500",
    accent: "text-fuchsia-600 dark:text-fuchsia-400",

    // Background classes
    backgroundPrimary: "bg-white dark:bg-gray-950",
    backgroundSecondary: "bg-gray-50 dark:bg-gray-900",
    backgroundSurface: "bg-gray-50 dark:bg-gray-800",
    backgroundTertiary: "bg-violet-50/60 dark:bg-gray-800",
    backgroundAccent: "bg-violet-600 dark:bg-violet-700",
    backgroundPrimaryHover: "hover:bg-gray-50 dark:hover:bg-gray-900",
    decorativeBackground:
      "bg-gradient-to-br from-violet-50 to-fuchsia-50 dark:from-gray-900 dark:to-gray-950",

    // Shared alternates (same across palettes)
    backgroundSoft: "bg-gray-50 dark:bg-gray-900",
    backgroundLighter: "bg-white dark:bg-gray-900",

    backgroundPattern: "bg-violet-100 dark:bg-gray-800",
    backgroundPrimaryColor: "bg-violet-600 dark:bg-violet-500",
    backgroundSecondaryColor: "bg-violet-800 dark:bg-violet-700",
    backgroundAccentColor: "bg-fuchsia-500 dark:bg-fuchsia-400",
    backgroundPrimarySubtle: "bg-violet-50 dark:bg-violet-950",

    // Card backgrounds
    backgroundCard: "bg-white dark:bg-gray-950",
    backgroundElevated: "bg-white dark:bg-gray-800",
    backgroundHighlighted:
      "bg-gradient-to-br from-white to-violet-50 dark:from-gray-800 dark:to-gray-900",

    // Text classes
    textPrimary: "text-gray-800 dark:text-gray-100",
    textSecondary: "text-gray-600 dark:text-gray-300",
    textMuted: "text-gray-500 dark:text-gray-400",
    textInverse: "text-white dark:text-gray-900",
    textAccent: "text-violet-700 dark:text-violet-400",
    textOnAccent: "text-white",

    // Text on dark backgrounds
    textPrimaryOnDark: "text-gray-100",
    textSecondaryOnDark: "text-gray-300",
    textMutedOnDark: "text-gray-400",

    // Icon classes
    iconText: "text-violet-600 dark:text-violet-400",
    iconBackgroundFilled: "bg-violet-100 dark:bg-violet-950",
    iconBackgroundGradient: "bg-gradient-to-br from-violet-500 to-fuchsia-500",
    iconBackgroundDefault: "bg-violet-50 dark:bg-violet-950/50",
    iconBorder: "border-violet-200 dark:border-violet-800",

    // Border classes
    borderDefault: "border-violet-200 dark:border-gray-700",
    borderLight: "border-violet-100 dark:border-gray-800",
    borderStrong: "border-violet-300 dark:border-gray-600",
    borderPrimary: "border-violet-700 dark:border-violet-500",
    borderSecondary: "border-violet-900 dark:border-violet-700",
    borderBottom: "border-b border-violet-200 dark:border-gray-700",
    borderAccent: "border-violet-600 dark:border-violet-400",

    // Badge classes
    badgeBackground: "bg-violet-100 dark:bg-violet-950",
    badgeText: "text-violet-700 dark:text-violet-300",

    // Shadow classes
    shadowPrimary: "shadow-violet-500/20",
    shadowDefault: "shadow-sm",
    shadowHover: "hover:shadow-lg hover:shadow-violet-500/10",
    shadowSmall: "shadow-sm",
    shadowMedium: "shadow-md",
    shadowLarge: "shadow-lg",
    shadowXLarge: "shadow-xl",
    shadowGlow: "shadow-[0_0_30px_rgba(139,92,246,0.30)]", // violet-500 glow
    shadowInset: "shadow-inner",

    // Interactive states
    hoverBackground: "hover:bg-violet-50 dark:hover:bg-gray-800",
    activeBackground: "active:bg-violet-100 dark:active:bg-gray-700",
    accentPrimary:
      "bg-violet-600 hover:bg-violet-700 dark:bg-violet-700 dark:hover:bg-violet-600",

    // Creative Bold Hero classes
    backgroundBold: "bg-violet-400 dark:bg-violet-600",
    textEmphasis: "text-amber-600 dark:text-amber-400",

    // Gradient classes
    gradientPrimary: "bg-gradient-to-r from-violet-600 to-violet-700",
    gradientSecondary: "bg-gradient-to-br from-violet-700 to-violet-900",
    gradientAccent: "bg-gradient-to-r from-violet-500 to-fuchsia-500",
    gradientSubtle:
      "bg-gradient-to-br from-violet-50 to-fuchsia-50 dark:from-gray-900 dark:to-gray-950",
    gradientVibrant:
      "bg-gradient-to-r from-violet-600 via-fuchsia-500 to-violet-600",
    gradientHero:
      "bg-gradient-to-br from-violet-600 via-fuchsia-500 to-violet-700",

    // Border radius classes
    roundedSmall: "rounded",
    roundedMedium: "rounded-md",
    roundedLarge: "rounded-lg",
    roundedXLarge: "rounded-xl",
    roundedFull: "rounded-full",

    // Opacity classes
    opacityDisabled: "opacity-50",
    opacityHover: "opacity-80",
    opacityBackdrop: "opacity-60",
    opacitySubtle: "opacity-30",

    // Transition classes
    transitionFast: "transition-all duration-150",
    transitionNormal: "transition-all duration-300",
    transitionSlow: "transition-all duration-500",

    // Z-index classes
    zDropdown: "z-10",
    zModal: "z-50",
    zOverlay: "z-40",
    zTooltip: "z-[60]",
    zFixed: "z-30",

    // Status colors (kept consistent across palettes)
    error: "text-red-600 dark:text-red-400",
    success: "text-green-600 dark:text-green-400",
    warning: "text-amber-600 dark:text-amber-400",
    info: "text-blue-600 dark:text-blue-400",

    errorBg: "bg-red-100 dark:bg-red-950",
    successBg: "bg-green-100 dark:bg-green-950",
    warningBg: "bg-amber-100 dark:bg-amber-950",
    infoBg: "bg-blue-100 dark:bg-blue-950",
  },
};
